/**
 * /connectors — user-mode view of connectors, three-section layout.
 *
 * Per Ean 2026-06-10 (item 3 of punt-list close-out): mirror the admin
 * /admin/connectors three-section pattern (Installed / Available / Coming
 * soon) on user pillar pages, so users see the full picture of what their
 * workspace has, what they can request, and what's on the roadmap.
 *
 * User-side semantics differ from admin-side: users can't install or assign;
 * they can only use what's assigned to them or request access. So the three
 * sections are:
 *
 *   1. Yours — connectors assigned to me (use today via Claude / ChatGPT / etc.)
 *   2. Available in your workspace — installed for the account but not
 *      assigned to me (request access from admin)
 *   3. Coming soon — preview templates Olytic will ship next (give feedback)
 *
 * Data source: /api/catalog already returns is_mine + is_installed + is_preview
 * flags for the requesting user. One fetch is enough.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useSession } from '../hooks/useSession.tsx';
import {
  fetchCatalog,
  fetchMyConnectors,
  ApiError,
  type CatalogTemplate,
  type MyConnectorDetailed,
} from '../lib/api.ts';
import { ConnectorLogo } from '../components/ConnectorLogo.tsx';
import { ObjectIcon } from '../components/ObjectIcon.tsx';
import { OAuthStatusPill } from '../components/StatusPill.tsx';
import { SkeletonCardGrid } from '../components/Skeleton.tsx';
import { useDelayedFlag } from '../hooks/useDelayedFlag.ts';
import { EmptyState } from '../components/EmptyState.tsx';
import { RequestModal } from '../components/RequestModal.tsx';

export function Connectors() {
  const { user } = useSession();
  const navigate = useNavigate();
  const [mine, setMine] = useState<MyConnectorDetailed[] | null>(null);
  // Per Ean 2026-06-10 card-density: collapse each section to 6 tiles by
  // default; "Show all" expands. Hidden when <= 6 items (no toggle needed).
  const [showAllYours, setShowAllYours] = useState(false);
  const [showAllRequest, setShowAllRequest] = useState(false);
  const [catalog, setCatalog] = useState<CatalogTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requestSlug, setRequestSlug] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchMyConnectors(), fetchCatalog()])
      .then(([mineData, catData]) => {
        if (cancelled) return;
        setMine(mineData.connectors);
        setCatalog(catData.templates);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Could not load connectors.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // D-156 catalog IA: partition into the 3-bucket user-mode projection.
  //   Bucket 1 "What you can use" (state 1)        — yoursRows
  //   Bucket 2 "Request access"   (state 2)        — availableRows (instance
  //                                                  exists, no assignment)
  //   Bucket 3 "Public catalog"   (states 3 + 4)   — publicCatalogRows.
  //                                                  state 3 (is_installed=false &&
  //                                                  is_preview=false) renders with
  //                                                  "Available" chip + Request install
  //                                                  action. state 4 (is_preview=true)
  //                                                  renders with "Coming soon" chip +
  //                                                  Notify me action. Same bucket,
  //                                                  two card variants per D-156.
  //
  // Sub-agent RRRR Track 1 fix (2026-07-29): source Bucket 1 ("yours") from
  // `/api/me/connectors` directly, not from catalog.is_mine. `/api/catalog`
  // applies the D-199 marketplace-eligibility filter (Pattern A + B, verified,
  // owner_account_id NULL), which intentionally excludes BYO Pattern B
  // templates (`owner_account_id IS NOT NULL`) and Pattern C templates
  // (`upstream_auth_mode='none'`). Those templates can still be assigned to a
  // user — the workspace owner installs them via /admin/connectors/byo — and
  // when they are, the assignment must surface in the user's "What you can
  // use" bucket. Prior logic joined mine → catalog via is_mine, which meant
  // any BYO/Pattern C assignment was silently dropped from the /connectors
  // list. Bucket 2 (request access) still comes from catalog, which is
  // correct — request-access flows work off marketplace-eligible templates
  // only (users can't request installs of BYO templates that only exist in
  // their workspace).
  const sections = useMemo(() => {
    if (catalog === null || mine === null) {
      return { yours: [], requestAccess: [], publicCatalog: [] };
    }
    // Bucket 1: every row from /api/me/connectors, unfiltered. Assignment
    // existence is the source of truth for "what you can use", not catalog
    // eligibility.
    const yoursRows: MyConnectorDetailed[] = [...mine];
    const mineTemplateNames = new Set(mine.map((c) => c.template_name));
    const requestAccessRows: CatalogTemplate[] = [];
    const publicCatalogRows: CatalogTemplate[] = [];
    for (const t of catalog) {
      // Skip anything already surfaced in Bucket 1 (defensive — catalog rows
      // with is_mine=true would double-render).
      if (t.is_mine || mineTemplateNames.has(t.name)) continue;
      if (t.is_installed) {
        // State 2: instance on workspace, no assignment for me yet.
        requestAccessRows.push(t);
        continue;
      }
      // State 3 (active, uninstalled) OR State 4 (coming_soon) — both
      // surface in Public catalog with chip + action differentiation.
      publicCatalogRows.push(t);
    }
    return { yours: yoursRows, requestAccess: requestAccessRows, publicCatalog: publicCatalogRows };
  }, [catalog, mine]);

  if (user === null) return null;

  const loaded = mine !== null && catalog !== null;

  return (
    <div>
      <div className="lightning-highlight">
        <div className="lightning-highlight-left">
          <ObjectIcon kind="connectors" size="lg" />
          <div className="lightning-highlight-text">
            <h1 className="lightning-title">My connectors</h1>
            <p
              className="muted"
              style={{ margin: '6px 0 0', fontSize: '13px', maxWidth: '72ch', lineHeight: 1.45 }}
            >
              Use these in Olytic chat, or through Claude Desktop, ChatGPT, or any MCP-aware AI client. See what your workspace has, what you can request, and what's on the way.
            </p>
          </div>
        </div>
        <div className="lightning-highlight-actions">
          <Link to="/market?tab=connectors" className="btn btn-secondary">
            Browse market
          </Link>
          <Link to="/chat" className="btn btn-primary">
            Go to chat
          </Link>
        </div>
      </div>

      {error !== null && (
        <div className="callout callout-danger" style={{ marginBottom: '16px' }}>
          <p className="callout-body">{error}</p>
        </div>
      )}

      {!loaded && error === null && <ConnectorsSkeleton />}

      {loaded && (
        <>
          <section className="catalog-section">
            <div className="catalog-section-heading">
              <h2 className="catalog-section-title">
                What you can use <span className="catalog-section-count">({sections.yours.length})</span>
              </h2>
              <p className="catalog-section-helper">
                Assigned to you. Click any tile to see the tools, the OAuth state, and how to use it from Olytic chat or your other AI clients.
              </p>
              {sections.yours.length > 6 && (
                <button type="button" className="btn-link" onClick={() => setShowAllYours((v) => !v)} style={{ fontSize: 12, marginTop: 4 }}>
                  {showAllYours ? 'Show fewer' : `Show all (${sections.yours.length})`}
                </button>
              )}
            </div>
            {sections.yours.length === 0 ? (
              // Push 5-F (2026-07-11): migrate legacy `cta` -> structured `primaryAction`.
              <EmptyState
                kind="connectors"
                title="No connectors assigned to you yet"
                body="Browse the market to install one, or ask your admin to assign one from the workspace list below."
                primaryAction={{ label: 'Browse the market', to: '/market?tab=connectors' }}
              />
            ) : (
              <div className="tile-grid">
                {(showAllYours ? sections.yours : sections.yours.slice(0, 6)).map((c) => (
                  <button
                    key={c.instance_id}
                    type="button"
                    className="tile"
                    onClick={() => navigate(`/connectors/${c.short_id}`)}
                  >
                    <div className="tile-header">
                      <ConnectorLogo
                        slug={c.template_name}
                        name={c.display_name}
                        logoUrl={c.logo_url}
                        size="md"
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p className="tile-name">{c.display_name}</p>
                        <p className="tile-meta">
                          {c.tool_count} tool{c.tool_count === 1 ? '' : 's'}
                        </p>
                      </div>
                    </div>
                    {c.description !== null && <p className="tile-tagline">{c.description}</p>}
                    <div className="tile-footer">
                      <OAuthStatusPill status={c.oauth_status} />
                      <span className="muted" style={{ fontSize: '12px' }}>Open →</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className="catalog-section">
            <div className="catalog-section-heading">
              <h2 className="catalog-section-title">
                What you could use <span className="catalog-section-count">({sections.requestAccess.length})</span>
              </h2>
              <p className="catalog-section-helper">
                Installed in your workspace but not assigned to you. Ask your admin for access.
              </p>
              {sections.requestAccess.length > 6 && (
                <button type="button" className="btn-link" onClick={() => setShowAllRequest((v) => !v)} style={{ fontSize: 12, marginTop: 4 }}>
                  {showAllRequest ? 'Show fewer' : `Show all (${sections.requestAccess.length})`}
                </button>
              )}
            </div>
            {sections.requestAccess.length === 0 ? (
              // Push 5-F (2026-07-11): migrate legacy `cta` -> structured `secondaryAction`.
              <EmptyState
                kind="connectors"
                title="Nothing else is installed in your workspace"
                body="Browse the market to suggest a new connector for your admin to install."
                secondaryAction={{ label: 'Browse the market', to: '/market?tab=connectors' }}
              />
            ) : (
              <div className="tile-grid">
                {(showAllRequest ? sections.requestAccess : sections.requestAccess.slice(0, 6)).map((t) => (
                  <div key={t.id} className="tile tile-static">
                    <div className="tile-header">
                      <ConnectorLogo
                        slug={t.name}
                        name={t.display_name}
                        logoUrl={t.logo_url}
                        size="md"
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p className="tile-name">{t.display_name}</p>
                        <p className="tile-meta">Installed in workspace</p>
                      </div>
                    </div>
                    {t.description !== null && <p className="tile-tagline">{t.description}</p>}
                    <div className="tile-footer">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRequestSlug(t.name);
                        }}
                      >
                        Request access
                      </button>
                      <Link
                        to={`/market/connectors/${t.short_id}`}
                        className="muted"
                        style={{ fontSize: '12px' }}
                      >
                        Details →
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* D-159: Public catalog (states 3+4) moved to /catalog. The Catalog
             top-level nav is the marketplace; pillar pages stay workspace-scoped. */}

          <div className="trust-whisper trust-whisper--footer">
            Your AI can only act through the connectors above · Permissions follow you, not the model
          </div>
        </>
      )}

      <RequestModal
        open={requestSlug !== null}
        onClose={() => setRequestSlug(null)}
        destination="admin"
        initialKind="connector_access"
        {...(requestSlug !== null ? { initialTemplateName: requestSlug } : {})}
      />
    </div>
  );
}

/**
 * ConnectorsSkeleton — pillar-page placeholder. Two sections of tile-grid
 * skeletons; mirrors the live "What you can use" + "What you could use"
 * layout so the page reads as becoming, not flashing. 200ms delay so fast
 * loads never flicker.
 */
function ConnectorsSkeleton() {
  const show = useDelayedFlag(true);
  if (!show) return <div style={{ minHeight: '40vh' }} />;
  return (
    <>
      <section className="catalog-section">
        <SkeletonCardGrid count={6} />
      </section>
      <section className="catalog-section">
        <SkeletonCardGrid count={3} />
      </section>
    </>
  );
}
